import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconType } from '../../../../../shared/services/icon.service';
import { formatLocalISODate } from '../../../../../shared/utils/date-format.util';
import {
  GENDER_OPTIONS,
  SALUTATION_OPTIONS,
} from '../profile-constants';
import type {
  UserGender,
  UserSalutation,
  User,
} from '../../../../shared/UserInterface';
import type { ProfileUpdateRequest } from '../../../../models/profile.interface';

interface GeneralFormShape {
  salutation: UserSalutation | '';
  firstName: string;
  lastName: string;
  displayName: string;
  birthdate: string;
  gender: UserGender | '';
}

@Component({
  selector: 'app-general-info',
  standalone: true,
  imports: [ReactiveFormsModule, IconComponent],
  templateUrl: './general-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../_collapsible-card.css'],
})
export class GeneralInfoComponent {
  @Input({ required: true }) set user(value: User | null | undefined) {
    this._user = value;
    if (!value) return;
    // Don't clobber in-progress edits when the parent's currentUser signal
    // refreshes (e.g. after a sibling section saves). Collapsed cards always
    // re-sync; the saving card accepts the fresh payload via `isSaving()`.
    if (this.isExpanded() && this.form.dirty && !this.isSaving()) return;
    this.populateForm(value);
  }
  get user(): User | null | undefined {
    return this._user;
  }
  private _user: User | null | undefined;

  @Output() save = new EventEmitter<Partial<ProfileUpdateRequest>>();

  private fb = inject(FormBuilder);

  readonly IconType = IconType;
  readonly salutationOptions = SALUTATION_OPTIONS;
  readonly genderOptions = GENDER_OPTIONS;
  // Local calendar date — `toISOString().slice(0,10)` would shift the cap
  // by a day at midnight in non-UTC zones (off-by-one bug). `new Date()` is
  // always valid, so the non-null assertion is safe.
  readonly todayIso = formatLocalISODate(new Date())!;

  isExpanded = signal(false);
  isSaving = signal(false);

  form: FormGroup = this.fb.group({
    salutation: [''],
    firstName: ['', [Validators.maxLength(100)]],
    lastName: ['', [Validators.maxLength(100)]],
    displayName: ['', [Validators.maxLength(100)]],
    birthdate: [''],
    gender: [''],
  });

  toggleExpanded(): void {
    this.isExpanded.update((v) => !v);
    if (this.isExpanded() && this._user) {
      this.populateForm(this._user);
    }
  }

  onSubmit(): void {
    if (!this.form.valid || this.isSaving()) return;
    const diff = this.collectDiff();
    if (Object.keys(diff).length === 0) {
      this.isExpanded.set(false);
      return;
    }
    this.isSaving.set(true);
    this.save.emit(diff);
  }

  onSaveComplete(success: boolean): void {
    this.isSaving.set(false);
    if (success) {
      this.isExpanded.set(false);
    }
  }

  hasError(field: keyof GeneralFormShape, error: string): boolean {
    const control = this.form.get(field);
    return !!(control?.touched && control?.hasError(error));
  }

  /** True when the current form value differs from the loaded user. */
  hasChanges(): boolean {
    return Object.keys(this.collectDiff()).length > 0;
  }

  /** True when the given field is part of the current pending diff. */
  isFieldChanged(field: keyof GeneralFormShape): boolean {
    return field in this.collectDiff();
  }

  private populateForm(user: User): void {
    this.form.reset({
      salutation: user.salutation ?? '',
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      displayName: user.displayName ?? '',
      birthdate: user.birthdate ?? '',
      gender: user.gender ?? '',
    });
  }

  /**
   * Build a partial update containing only the fields whose value differs
   * from the user's current data. Empty string => clear field on backend.
   */
  private collectDiff(): Partial<ProfileUpdateRequest> {
    const value = this.form.value as GeneralFormShape;
    const diff: Partial<ProfileUpdateRequest> = {};
    const u = this._user;
    if (!u) return diff;

    if ((value.salutation || null) !== (u.salutation ?? null)) {
      diff.salutation = value.salutation || null;
    }
    if (value.firstName !== (u.firstName ?? '')) {
      diff.firstName = value.firstName;
    }
    if (value.lastName !== (u.lastName ?? '')) {
      diff.lastName = value.lastName;
    }
    if (value.displayName !== (u.displayName ?? '')) {
      diff.displayName = value.displayName;
    }
    if (value.birthdate !== (u.birthdate ?? '')) {
      diff.birthdate = value.birthdate;
    }
    if ((value.gender || null) !== (u.gender ?? null)) {
      diff.gender = value.gender || null;
    }
    return diff;
  }
}
